import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import isEqual from 'react-fast-compare';
import {
  ArrayFieldApiContext,
  ArrayFieldStateContext,
  ArrayFieldItemApiContext,
  ArrayFieldItemStateContext,
} from '../Context';
import FormProvider from '../components/FormProvider';
import getDict from '../utils/getDict';
import useLayoutEffect from './useIsomorphicLayoutEffect';

export default function useArrayFieldItem({ item, ...userProps }) {
  const { initialValues, values: itemValues } = item;
  const { itemRefs, schema } = useContext(ArrayFieldStateContext);
  const { move, moveDown, moveUp, remove, update, setInitialValues } = useContext(
    ArrayFieldApiContext
  );
  const formContextRef = useRef();
  const itemRef = useRef();
  const itemValuesRef = useRef();
  const isMounted = useRef(false);
  const arrayFieldItemState = item;

  itemValuesRef.current = itemValues;
  itemRef.current = item;

  const [arrayFieldItemApi] = useState({
    move(nextIndex) {
      const { key } = itemRef.current;

      move(key, nextIndex);
    },
    moveDown() {
      const { key } = itemRef.current;

      moveDown(key);
    },
    moveUp() {
      const { key } = itemRef.current;

      moveUp(key);
    },
    remove() {
      const { key } = itemRef.current;

      remove(key);
    },
    reset() {
      formContextRef.current.reset();
      const { key } = itemRef.current;
      update(key, formContextRef.current.getState().values);
    },
    update(values) {
      const { key } = itemRef.current;

      update(key, values);
    },
  });

  useEffect(() => {
    const { key } = itemRef.current;

    itemRefs.set(key, formContextRef);

    isMounted.current = true;

    const { values: mountedValues } = formContextRef.current.getState();

    if (!isEqual(mountedValues, itemValuesRef.current)) {
      setInitialValues(key, mountedValues);
    }

    return () => {
      itemRefs.delete(key);

      isMounted.current = false;
    };
  }, []);

  const updateCallback = useCallback(({ values }) => {
    if (!isEqual(values, itemValuesRef.current)) {
      arrayFieldItemApi.update(values);
    }
  }, []);

  useLayoutEffect(() => {
    if (!isMounted.current) {
      return;
    }

    if (!isEqual(itemValues, formContextRef.current.values)) {
      const valuesDict = getDict(itemValues);

      Object.entries(valuesDict).forEach(([dataPath, value]) => {
        formContextRef.current.setValue(dataPath, value);
      });
    }
  }, [itemValues]);

  const render = (children) => (
    <ArrayFieldItemApiContext.Provider value={arrayFieldItemApi}>
      <ArrayFieldItemStateContext.Provider value={arrayFieldItemState}>
        <FormProvider
          initialValues={initialValues}
          onChange={updateCallback}
          formContextRef={formContextRef}
          validateOnChange={false}
          validateOnBlur={false}
          schema={schema}
        >
          {children}
        </FormProvider>
      </ArrayFieldItemStateContext.Provider>
    </ArrayFieldItemApiContext.Provider>
  );

  return {
    arrayFieldItemApi,
    arrayFieldItemState,
    render,
    userProps,
  };
}
